import clsx from 'clsx';
import type { InputHTMLAttributes, TextareaHTMLAttributes } from 'react';
import type { Size } from './layout';
import { isDefined, isStringNonEmpty } from '@/utils/assertions';
type InputProps = Omit<{ tag: 'input' } & InputHTMLAttributes<HTMLInputElement>, 'size'>;
type TextareaProps = Omit<
  { tag: 'textarea' } & TextareaHTMLAttributes<HTMLTextAreaElement>,
  'size'
>;
export type TextFieldCommonProps = {
  description?: string;

  size?: Size;
  outline?: boolean;

  validationState?: 'default' | 'edited' | 'valid' | 'warning' | 'invalid';
  validationText?: string;

  icon?: string;
  iconPosition?: 'start' | 'end';
};
export type TextFieldProps = (InputProps | TextareaProps) & TextFieldCommonProps;

/**
 * Reusable component to share behavior between textarea and input
 *
 * @example
 * <FieldWrapper purpose="primary" size="lg" />
 */
export const TextField = (props: TextFieldProps) => {
  const {
    value,
    placeholder,
    description,
    size = 'md',
    outline: isOutline = false,
    validationState,
    validationText,
    icon,
    iconPosition = 'start',
    className,
    ...otherProps
  } = props;

  const inputClassName = clsx(
    ['form-control', size !== undefined ? `form-control-${size}` : undefined, className],
    {
      'is-edited': validationState === 'edited',
      'is-valid': validationState === 'valid',
      'is-warning': validationState === 'warning',
      'is-invalid': validationState === 'invalid',

      'form-control-outline': isOutline,
    },
  );
  const Field =
    otherProps.tag === 'input' ? (
      <input
        type="text"
        placeholder={placeholder}
        value={value}
        className={inputClassName}
        {...otherProps}
      />
    ) : (
      <textarea
        placeholder={placeholder}
        value={value}
        className={inputClassName}
        {...otherProps}
      />
    );
  return (
    <div className="form-group">
      {isDefined(icon) ? (
        <div className="input-group">
          <div className={`input-icon-${iconPosition}`}>
            <em className="icon">{icon}</em>
          </div>
          {Field}
        </div>
      ) : (
        Field
      )}

      {validationState === 'edited' && (
        <span className="edited-feedback">
          <em className="icon">warning</em>
          {validationText}
        </span>
      )}

      {validationState === 'valid' && (
        <span className="valid-feedback">
          <em className="icon">done</em>
          {validationText}
        </span>
      )}

      {validationState === 'invalid' && (
        <span className="invalid-feedback">
          <em className="icon">close</em>
          {validationText}
        </span>
      )}

      {validationState === 'warning' && (
        <span className="warning-feedback">
          <em className="icon">warning</em>
          {validationText}
        </span>
      )}

      {isStringNonEmpty(description) && <span className="form-text">{description}</span>}
    </div>
  );
};
